/**
 * Medicine lines of a prescription: the doctor's create-Rx and the pharmacist's digitise form share
 * it. Fields are kept as typed and checked against the API's rules (lib/rxLines.ts, DOC-08/09); a
 * medicine already on the prescription isn't added again (D-18).
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { searchCatalogueMedicines } from "../api/catalogue";
import { addProblem, newRxLine, rxLineProblems, unitLabel, type RxLineDraft } from "../lib/rxLines";
import { useUnitKinds } from "../lib/useUnitKinds";
import { IconButton, RowActions, TrashIcon } from "./ui/IconButton";
import { Select } from "./ui/Select";

const FOOD = ["before_food", "after_food"];
const SLOTS = ["morning", "afternoon", "night"];

export function RxLineEditor({ lines, setLines }: { lines: RxLineDraft[]; setLines: (l: RxLineDraft[]) => void }) {
  const [q, setQ] = useState("");
  const [notice, setNotice] = useState("");
  const units = useUnitKinds();
  const { data: meds = [] } = useQuery({
    // Trimmed: "para " found nothing (DOC-06); the API matches the name or the brand.
    queryKey: ["cat-meds", q.trim()],
    queryFn: () => searchCatalogueMedicines(q.trim()),
    enabled: q.trim().length >= 1,
  });

  const addLine = (m: { id: string; name: string; unit_kind: string; schedule_class: string }) => {
    const problem = addProblem(lines, m.id);
    setNotice(problem);
    if (!problem) setLines([...lines, newRxLine(m)]);
    setQ("");
  };
  const update = (i: number, patch: Partial<RxLineDraft>) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const toggleSlot = (i: number, slot: string) => {
    const cur = lines[i].time_of_day;
    update(i, { time_of_day: cur.includes(slot) ? cur.filter((s) => s !== slot) : [...cur, slot] });
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <input value={q} onChange={(e) => { setQ(e.target.value); setNotice(""); }} placeholder="Search medicine…" className="w-full rounded-lg border px-3 py-1.5 text-sm" />
        {q.trim().length >= 1 && (
          <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-xl border border-line bg-surface py-1 shadow-raised">
            {meds.map((m) => (
              <li key={m.id}>
                <button type="button" onClick={() => addLine(m)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm text-ink hover:bg-brand-50">
                  <span>
                    {m.name}
                    {m.brand && <span className="ml-1 text-xs text-ink-muted">· {m.brand}</span>}
                  </span>
                  <span className="text-xs text-ink-muted">{m.schedule_class === "none" ? "OTC" : `Sch ${m.schedule_class}`}</span>
                </button>
              </li>
            ))}
            {meds.length === 0 && <li className="px-3 py-2 text-sm text-ink-muted">No matches.</li>}
          </ul>
        )}
      </div>
      {notice && <p role="alert" className="text-sm text-amber-700">{notice}</p>}

      {lines.map((l, i) => {
        const problems = rxLineProblems(l);
        return (
          <div key={l.medicine_id} className="rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{l.medicine_name}</span>
              <RowActions>
                <IconButton label="Remove" tone="danger" onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>
                  <TrashIcon />
                </IconButton>
              </RowActions>
            </div>
            {/* On a touch screen every field of the line is a finger's height (RESP-21). */}
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm pointer-coarse:[&_input]:py-2 pointer-coarse:[&_.select-sm]:py-2 sm:grid-cols-5">
              <Field label="Qty" value={l.quantity} inputMode="numeric" problem={problems.quantity} onChange={(v) => update(i, { quantity: v })} />
              <Field label="Dose" value={l.dosage_value} inputMode="decimal" problem={problems.dosage_value} onChange={(v) => update(i, { dosage_value: v })} />
              {/* The medicine's own unit to start with; a cream's dose is an "application" (DOC-18, D-21). */}
              <label className="flex flex-col gap-1 text-xs text-gray-500">
                Unit
                <Select
                  value={l.dosage_unit}
                  options={(units.includes(l.dosage_unit) ? units : [l.dosage_unit, ...units]).map((u) => ({ value: u, label: unitLabel(u) }))}
                  onChange={(u) => update(i, { dosage_unit: u })}
                  className="rounded-sm py-1 pl-2"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-gray-500">
                Food
                <Select
                  value={l.food_relation}
                  options={FOOD.map((f) => ({ value: f, label: f.replace("_", " ") }))}
                  onChange={(f) => update(i, { food_relation: f })}
                  className="rounded-sm py-1 pl-2"
                />
              </label>
              <Field label="Days" value={l.duration_days} inputMode="numeric" placeholder="Optional" problem={problems.duration_days} onChange={(v) => update(i, { duration_days: v })} />
            </div>
            <div className="mt-2 flex gap-2 text-xs">
              {SLOTS.map((s) => (
                <button key={s} type="button" onClick={() => toggleSlot(i, s)} className={`rounded-full px-3 py-1 pointer-coarse:min-h-9 pointer-coarse:px-4 ${l.time_of_day.includes(s) ? "bg-brand text-brand-fg" : "border"}`}>
                  {s}
                </button>
              ))}
            </div>
            {problems.time_of_day && <p className="mt-1 text-xs text-red-600">{problems.time_of_day}</p>}
          </div>
        );
      })}
      {lines.length === 0 && <p className="text-sm text-gray-400">No medicines added yet.</p>}
    </div>
  );
}

function Field({
  label,
  value,
  inputMode,
  placeholder,
  problem,
  onChange,
}: {
  label: string;
  value: string;
  inputMode: "numeric" | "decimal";
  placeholder?: string;
  problem: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-gray-500">
      {label}
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        aria-invalid={!!problem}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full min-w-0 rounded-sm border px-2 py-1 text-gray-900 ${problem ? "border-red-400" : ""}`}
      />
      {problem && <span className="text-red-600">{problem}</span>}
    </label>
  );
}
